import { queryOptions } from '@tanstack/vue-query'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import type { LocationQuery, LocationQueryValue } from 'vue-router'
import type {
  BrandFilters,
  OtherFilters,
  PriceRangeFilter,
  ProductFilter,
} from '~/types/api/data/product/filter/filter.types'
import {
  getValidatedCurrentFilters,
  getValidatedCurrentPriceRange,
} from '~/utils/product-filter/filter-helpers'

export const useGetVariantsQuery = (
  pageSize: Ref<number>,
  pageNumber: Ref<number>,
  productGroupKey: Ref<string>,
  productFilter: Ref<ProductFilter>,
  otherOptions?: Ref<NitroFetchOptions<NitroFetchRequest>>
) => {
  const { $variantsRepository } = useNuxtApp()
  const route = useRoute()
  const { slugUrl } = useBaseRoutes()
  const { queryFilterType } = useQueryHelper()
  const { getCategoryPathFromRoute } = useProductListingRoutes()
  const { resolvePriceRange } = useProductListingFiltersStore()
  // To set locale and country for preview mode in order to get the correct data from CS
  const { previewHeaders } = usePreviewLayoutMeta()

  const options = computed(() => {
    const { brandFilters, otherFilters, priceRangeFilter } = productFilter.value
    const categoryParams = getCategoryPathFromRoute()
    const queryFilterValue = productGroupKey.value || slugUrl.value

    return {
      query: {
        [`filter[${queryFilterType.value}]`]: queryFilterValue,
        'where[category]': categoryParams ? categoryParams : undefined,
        'where[brand]': getValidatedQuery(brandFilters, route.query.brands),
        'where[others]': getValidatedQuery(otherFilters, route.query.others),
        ...getValidatedPriceQuery(
          resolvePriceRange(priceRangeFilter),
          route.query
        ),
        'where[filter_type][0]': undefined, // TODO: Add mapping for typeFilters during category page development
        'page[number]': pageNumber.value,
        'page[size]': pageSize.value,
        sort: route.query.sort,
      },
      headers: {
        ...previewHeaders.value,
      },
      ...toValue(otherOptions),
    }
  })

  return queryOptions({
    queryKey: ['variants', options],
    queryFn: () => $variantsRepository.index(unref(options)),
  })
}

// Mini todo: move/reuse these functions elsewhere
function getValidatedQuery(
  filters: BrandFilters[] | OtherFilters[],
  query: LocationQueryValue | LocationQueryValue[]
) {
  if (!query) return undefined
  const validatedQuery = getValidatedCurrentFilters(
    filters,
    query.toString().split(',')
  ).join(',')
  return validatedQuery ? validatedQuery : undefined
}

function getValidatedPriceQuery(
  filter: PriceRangeFilter,
  query: LocationQuery
) {
  if (!query['min-price'] && !query['max-price']) return

  const validatedQuery = getValidatedCurrentPriceRange(filter, {
    'min-price': query['min-price']?.toString() ?? '',
    'max-price': query['max-price']?.toString() ?? '',
  })
  return {
    'where[min_price]': getConvertedPrice(validatedQuery['min-price']),
    'where[max_price]': getConvertedPrice(validatedQuery['max-price']),
  }
}

function getConvertedPrice(price: string) {
  const convertedPrice = Number(price) * 100
  return convertedPrice ? convertedPrice.toString() : undefined
}
